import { useState, useEffect } from 'react';
import axios from 'axios';

export default function ReportViewer({ report, onBack }) {
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const publicUrl = report.html_path 
    ? `https://8.217.57.57:8443${report.html_path}`
    : null;

  const coverUrl = report.cover_path
    ? `https://8.217.57.57:8443${report.cover_path}`
    : null;

  const pdfUrl = report.pdf_path
    ? `https://8.217.57.57:8443${report.pdf_path}`
    : null;

  const packageUrl = report.package_path
    ? `https://8.217.57.57:8443${report.package_path}`
    : null;

  const coverApiUrl = `/api/reports/${report.id}/cover`;
  const pdfApiUrl = `/api/reports/${report.id}/pdf`;
  const packageApiUrl = `/api/reports/${report.id}/package`;

  useEffect(() => {
    async function fetchNewsItems() {
      try {
        const response = await axios.get(`/api/reports/${report.id}`);
        setNewsItems(response.data.newsItems || []);
      } catch (error) {
        console.error('Failed to fetch news items:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchNewsItems();
  }, [report.id]);

  return (
    <div className="p-8">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="mb-6 flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium"
      >
        ← Back to Library
      </button>

      {/* Report Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-8 text-white mb-8">
        <h2 className="text-2xl font-semibold mb-3">{report.title}</h2>
        <div className="flex flex-wrap gap-4 text-sm opacity-90">
          <span>🏭 {report.industry_name || report.industry}</span>
          {report.business_category_name && (
            <span>📋 {report.business_category_name}</span>
          )}
          <span>📊 {report.news_type || report.newsType}</span>
          <span>📍 {report.region}</span>
        </div>
      </div>

      {/* Report Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <InfoCard label="Status" value={report.status} icon="📊" />
        <InfoCard label="Generated" value={formatDate(report.created_at || report.createdAt)} icon="📅" />
        <InfoCard label="Report ID" value={report.id.slice(0, 8) + '...'} icon="🆔" />
        <InfoCard label="News Items" value={newsItems.length} icon="📰" />
      </div>

      {/* Cover Image & Downloads */}
      {coverUrl && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">🖼️ Report Cover</h3>
            <div className="flex gap-2">
              <a
                href={coverApiUrl}
                download
                className="text-xs bg-slate-100 text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-200 transition-colors font-medium"
              >
                📥 Download Cover
              </a>
              {pdfUrl && (
                <a
                  href={pdfApiUrl}
                  download
                  className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  📄 Download PDF
                </a>
              )}
            </div>
          </div>
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-900">
            <img
              src={coverUrl}
              alt="Report Cover"
              className="w-full max-w-md mx-auto block"
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-4 mb-8">
        {publicUrl && (
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 bg-slate-900 text-white py-3 px-6 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors text-center shadow-sm"
          >
            🌐 Open Full Report
          </a>
        )}
        {pdfUrl && (
          <a
            href={pdfApiUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 bg-white border border-slate-300 text-slate-700 py-3 px-6 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors text-center"
          >
            📄 View PDF
          </a>
        )}
        {packageUrl && (
          <a
            href={packageApiUrl}
            className="flex-1 bg-white border border-slate-300 text-slate-700 py-3 px-6 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors text-center"
          >
            📦 Download Package
          </a>
        )}
        {!pdfUrl && (
          <button
            onClick={() => window.open(publicUrl, '_blank')}
            className="flex-1 bg-white border border-slate-300 text-slate-700 py-3 px-6 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            📥 Print / Export
          </button>
        )}
      </div>

      {/* News Items Table */}
      <div className="border border-slate-200 rounded-xl overflow-hidden mb-8">
        <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-900">📊 Intelligence Sources</h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block h-6 w-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mb-2"></div>
            <p className="text-slate-600 text-sm">Loading sources...</p>
          </div>
        ) : newsItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">URL</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {newsItems.map((item, index) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-500">{index + 1}</td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900 max-w-md truncate">
                      {item.title}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      <span className="px-2 py-1 bg-slate-100 rounded text-xs">{item.source}</span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <a href={item.url} target="_blank" rel="noopener" className="text-slate-600 hover:text-slate-900 truncate block max-w-xs">
                        {item.url}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-slate-500 text-sm">
            No source data available for this report
          </div>
        )}
      </div>

      {/* Preview Frame */}
      {publicUrl && (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
            <h4 className="text-sm font-medium text-slate-700">Report Preview</h4>
          </div>
          <iframe
            src={publicUrl}
            className="w-full"
            style={{ height: '600px' }}
            title="Report Preview"
          />
        </div>
      )}
    </div>
  );
}

function InfoCard({ label, value, icon }) {
  return (
    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
      <div className="text-xs text-slate-500 mb-1">
        {icon} {label}
      </div>
      <div className="text-base font-semibold text-slate-900 truncate">
        {value}
      </div>
    </div>
  );
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
